document.addEventListener("DOMContentLoaded", () => {
  let crossSellRendering = false;

  const debounce = (fn, ms) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  };

  /* ---- helpers to always get fresh DOM refs (supports drawer + cart page) ---- */
  const getCrossSellRoots = () =>
    Array.from(document.querySelectorAll("[data-cross-sell]"));

  const getCrossSellData = () => {
    const dataEl = document.querySelector("[data-cross-sell-data]");
    if (!dataEl) return null;
    try {
      const parsed = JSON.parse(dataEl.textContent);
      return parsed && parsed.length ? parsed : null;
    } catch (e) {
      return null;
    }
  };

  const getItemsElementFor = (root) => {
    if (root.closest("cart-drawer")) {
      return document.querySelector("cart-drawer-items");
    }
    // Cart page: cross-sell now lives inside main-cart-footer (sidebar),
    // not inside <cart-items>. Fall back to the global cart-items element
    // so the empty-cart check still works.
    return root.closest("cart-items") || document.querySelector("cart-items");
  };

  const showCrossSell = (el) => {
    if (!el) return;
    el.style.display = "block";
    requestAnimationFrame(() => {
      el.classList.add("is-visible");
    });
  };

  const hideCrossSell = (el) => {
    if (!el) return;
    el.classList.remove("is-visible");
    el.style.display = "none";
  };

  /* ---- cross-sell rendering ---- */
  const MAX_CROSS_SELL = 4;

  const renderCrossSell = () => {
    if (crossSellRendering) return;
    crossSellRendering = true;

    const roots = getCrossSellRoots();
    if (!roots.length) {
      crossSellRendering = false;
      return;
    }

    fetch("/cart.js")
      .then((r) => r.json())
      .then((cart) => {
        const freshRoots = getCrossSellRoots();
        freshRoots.forEach((root) => {
          const ic = root.querySelector("[data-cross-sell-items]");
          if (ic) ic.innerHTML = "";
          hideCrossSell(root);
        });

        if (!cart.items || cart.items.length === 0) return;

        const cartProductIds = new Set(
          cart.items.map((item) => item.product_id),
        );
        const gapCents = 500000 - cart.total_price;

        const allProducts = getCrossSellData();
        if (!allProducts) return;

        const candidates = [];
        for (const p of allProducts) {
          if (cartProductIds.has(p.id)) continue;
          if (!p.variants || !p.variants.length) continue;
          const price = Math.min(...p.variants.map((v) => v.price));
          if (price > 0) {
            candidates.push({ product: p, price });
          }
        }

        if (!candidates.length) return;

        /* When the free-shipping threshold is reached, show the cheapest items
           (cross-sell stays useful instead of disappearing). Otherwise, sort
           by closeness to the shipping gap as before. */
        if (gapCents > 0) {
          candidates.sort(
            (a, b) =>
              Math.abs(a.price - gapCents) - Math.abs(b.price - gapCents),
          );
        } else {
          candidates.sort((a, b) => a.price - b.price);
        }

        const finalProducts = candidates
          .slice(0, MAX_CROSS_SELL)
          .map((c) => c.product);
        if (!finalProducts.length) return;

        getCrossSellRoots().forEach((root) => {
          const itemsEl = getItemsElementFor(root);
          if (!itemsEl || itemsEl.classList.contains("is-empty")) return;
          const ic = root.querySelector("[data-cross-sell-items]");
          const tmpl = root.querySelector("[data-cross-sell-item]");
          if (!ic || !tmpl) return;
          // Switch heading once the free-shipping threshold is reached.
          const heading = root.querySelector("[data-cross-sell-heading]");
          if (heading) {
            heading.textContent =
              gapCents > 0
                ? "Add to unlock free shipping:"
                : "You may also like:";
          }
          showCrossSell(root);
          renderProducts(finalProducts, ic, tmpl);
        });
      })
      .catch(() => {})
      .finally(() => {
        crossSellRendering = false;
      });
  };
  const renderProducts = (products, container, template) => {
    const currencyCode =
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
        ? window.Shopify.currency.active
        : "PHP";
    const locale = (window.Shopify && window.Shopify.locale) || "en";

    products.forEach((product) => {
      const variant = product.variants && product.variants[0];
      if (!variant) return;

      const clone = template.content.cloneNode(true);
      const card = clone.querySelector(".cart-drawer__cross-sell-card");
      if (card) {
        card.dataset.productId = product.id;
        card.dataset.productUrl = product.url || "";
      }
      const link = clone.querySelector(".cart-drawer__cross-sell-link");
      if (link) link.href = product.url || "#";

      const img = clone.querySelector("img");
      if (img) {
        const src =
          typeof product.featured_image === "string"
            ? product.featured_image
            : product.featured_image?.src || "";
        img.src = src
          ? src.replace(/\.(jpg|jpeg|png|gif|webp)/i, "_200x200.$1")
          : "";
        img.alt = product.title || "";
      }

      const titleEl = clone.querySelector(".cart-drawer__cross-sell-title");
      if (titleEl) titleEl.textContent = product.title || "";

      const priceEl = clone.querySelector(".cart-drawer__cross-sell-price");
      if (priceEl) {
        try {
          let currency = currencyCode;
          if (
            variant.presentment_prices &&
            variant.presentment_prices.length > 0 &&
            variant.presentment_prices[0].price
          ) {
            currency =
              variant.presentment_prices[0].price.currency_code || currencyCode;
          }
          priceEl.textContent = new Intl.NumberFormat(locale, {
            style: "currency",
            currency: currency,
          }).format(variant.price / 100);
        } catch (e) {
          priceEl.textContent = (variant.price / 100).toFixed(2);
        }
      }

      container.appendChild(clone);
    });
  };

  /* ---- Free shipping suggestions ---- */

  const renderFreeShippingSuggestions = () => {
    const containers = Array.from(
      document.querySelectorAll("[data-shipping-gap]"),
    );
    if (!containers.length) return;

    fetch("/cart.js")
      .then((r) => r.json())
      .then((cart) => {
        if (!cart.items || cart.items.length === 0) return;
        const cartProductIds = new Set(cart.items.map((i) => i.product_id));

        const allProducts = getCrossSellData();
        if (!allProducts) return;

        const candidates = [];
        for (const p of allProducts) {
          if (cartProductIds.has(p.id)) continue;
          if (!p.variants || !p.variants.length) continue;
          const price = Math.min(...p.variants.map((v) => v.price));
          if (price > 0) {
            candidates.push({ product: p, price });
          }
        }
        if (!candidates.length) return;

        const currencyCode =
          window.Shopify &&
          window.Shopify.currency &&
          window.Shopify.currency.active
            ? window.Shopify.currency.active
            : "PHP";
        const locale = (window.Shopify && window.Shopify.locale) || "en";

        const formatPrice = (price, variant) => {
          try {
            let currency = currencyCode;
            if (
              variant.presentment_prices &&
              variant.presentment_prices.length > 0 &&
              variant.presentment_prices[0].price
            ) {
              currency =
                variant.presentment_prices[0].price.currency_code ||
                currencyCode;
            }
            return new Intl.NumberFormat(locale, {
              style: "currency",
              currency: currency,
            }).format(price / 100);
          } catch (e) {
            return (price / 100).toFixed(2);
          }
        };

        document
          .querySelectorAll("[data-shipping-gap]")
          .forEach((container) => {
            const gapAmount = parseInt(container.dataset.shippingGap, 10);
            if (isNaN(gapAmount) || gapAmount <= 0) return;

            const sorted = candidates
              .slice()
              .sort(
                (a, b) =>
                  Math.abs(a.price - gapAmount) -
                  Math.abs(b.price - gapAmount),
              );
            const products = sorted.slice(0, 2).map((c) => c.product);
            if (!products.length) return;

            const productLinks = products
              .map((product) => {
                const variant = product.variants && product.variants[0];
                if (!variant) return null;
                const priceText = formatPrice(variant.price, variant);
                return (
                  '<a href="' +
                  (product.url || "#") +
                  '" class="cart-drawer__free-shipping-suggestion-link">' +
                  product.title +
                  " (" +
                  priceText +
                  ")</a>"
                );
              })
              .filter(Boolean);

            if (!productLinks.length) return;

            let list;
            if (productLinks.length === 1) {
              list = productLinks[0];
            } else {
              const last = productLinks.pop();
              list = productLinks.join(", ") + " or " + last;
            }

            container.innerHTML = "";
            const msg = document.createElement("p");
            msg.className = "cart-drawer__free-shipping-suggestions-text";
            msg.innerHTML =
              "Complete your order \u2014 add " +
              list +
              " to unlock free Metro Manila shipping (with orders over <strong>₱5,000</strong>). Or pay a flat shipping rate starting <strong>₱105</strong> at checkout.";
            container.appendChild(msg);
          });
      })
      .catch(() => {});
  };

  const debouncedRenderCrossSell = debounce(renderCrossSell, 300);
  const debouncedRenderShipping = debounce(renderFreeShippingSuggestions, 300);

  /* ---- remove animation on click ---- */
  document.addEventListener("click", (e) => {
    const btn = e.target.closest(".cart-remove-button");
    if (btn) {
      const row = btn.closest(".cart-item");
      if (row) row.classList.add("cart-item--removing");
    }
  });

  /* ---- cross-sell Add to Bag click ---- */
  const addToCart = (variantId, button) => {
    if (button) {
      button.classList.add("loading");
      button.disabled = true;
      const spinner = button.querySelector(".loading__spinner");
      if (spinner) spinner.classList.remove("hidden");
    }
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((r) => {
        if (!r.ok) throw new Error("add_failed");
        return r.json();
      })
      .then(() => {
        if (typeof publish !== "undefined" && typeof PUB_SUB_EVENTS !== "undefined") {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-drawer-cross-sell" });
        }
        const drawer = document.querySelector("cart-drawer");
        const onCartPage =
          !!document.querySelector("cart-items") &&
          (!drawer || !drawer.classList.contains("active"));
        if (onCartPage) {
          // Server-rendered totals/bar on /cart — simplest correct refresh.
          window.location.reload();
          return;
        }
        if (typeof CartDrawer !== "undefined") {
          if (drawer && typeof drawer.renderContents === "function") {
            drawer.renderContents();
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (button) {
          button.classList.remove("loading");
          button.disabled = false;
          const spinner = button.querySelector(".loading__spinner");
          if (spinner) spinner.classList.add("hidden");
        }
      });
  };
  /* ---- cross-sell Add to Bag: direct add or product page ---- */
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-cross-sell-add]");
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const card = btn.closest(".cart-drawer__cross-sell-card");
    if (!card) return;
    const productUrl = card.dataset.productUrl || "";

    const allProducts = getCrossSellData();
    if (!allProducts) return;
    const productId = parseInt(card.dataset.productId, 10);
    const product = allProducts.find((p) => p.id === productId);
    if (!product || !product.variants || !product.variants.length) return;

    // Products with a choice to make (multiple variants and/or YMQ virtual
    // options) go to the product page. Single-variant products with no YMQ
    // options are added to the bag directly.
    if (product.variants.length > 1 || product.has_ymq_options) {
      if (productUrl) window.location.href = productUrl;
      return;
    }
    addToCart(product.variants[0].id, btn);
  });

  /* ---- initial render ---- */
  debouncedRenderCrossSell();
  debouncedRenderShipping();

  /* ---- patch CartDrawer.prototype.renderContents for add-to-cart ---- */
  const patchRenderContents = () => {
    if (typeof CartDrawer === "undefined") return;
    const origRender = CartDrawer.prototype.renderContents;
    if (!origRender || origRender.__crossSellPatched) return;

    CartDrawer.prototype.renderContents = function (...args) {
      const result = origRender.apply(this, args);
      setTimeout(() => {
        debouncedRenderCrossSell();
        debouncedRenderShipping();
      }, 100);
      return result;
    };
    CartDrawer.prototype.renderContents.__crossSellPatched = true;
  };

  patchRenderContents();

  /* ---- also listen for the pub/sub cartUpdate event ---- */
  if (
    typeof subscribe !== "undefined" &&
    typeof PUB_SUB_EVENTS !== "undefined"
  ) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      setTimeout(() => {
        debouncedRenderCrossSell();
        debouncedRenderShipping();
      }, 150);
    });
  }
});
